/**
 * Landing Features organism component
 * Showcases key features and capabilities of the Furniturr application
 */

// #region Imports
import { Box, Typography, Container, Card, CardContent, useTheme, Stack, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
    Build,
    Settings,
    Layers,
    BarChart,
    ViewInAr,
    Rotate90DegreesCcw,
    Category,
} from "@mui/icons-material";
// #endregion

// #region Types
/**
 * Feature item interface
 */
interface FeatureItem {
    id: string;
    icon: React.ReactNode;
    title: string;
    description: string;
    highlights: string[];
}

/**
 * Props for LandingFeatures organism component
 */
interface LandingFeaturesOrganismProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * LandingFeatures organism component showcasing key application features
 * Displays features in an organized grid layout with icons and descriptions
 *
 * @param props - The component props
 * @returns JSX element representing the features section
 */
function LandingFeaturesOrganism({ ...props }: LandingFeaturesOrganismProps) {
    const { t } = useTranslation("landing");
    const theme = useTheme();

    // Current features
    const CURRENT_FEATURES: FeatureItem[] = [
        {
            id: "multi-texture",
            icon: <Layers />,
            title: t("features.items.multiTexture.title"),
            description: t("features.items.multiTexture.description"),
            highlights: t("features.items.multiTexture.highlights", { returnObjects: true }) as string[],
        },
        {
            id: "cutting-methods",
            icon: <Build />,
            title: t("features.items.cuttingMethods.title"),
            description: t("features.items.cuttingMethods.description"),
            highlights: t("features.items.cuttingMethods.highlights", { returnObjects: true }) as string[],
        },
        {
            id: "smart-algorithms",
            icon: <Settings />,
            title: t("features.items.smartAlgorithms.title"),
            description: t("features.items.smartAlgorithms.description"),
            highlights: t("features.items.smartAlgorithms.highlights", { returnObjects: true }) as string[],
        },
        {
            id: "piece-management",
            icon: <Category />,
            title: t("features.items.pieceManagement.title"),
            description: t("features.items.pieceManagement.description"),
            highlights: t("features.items.pieceManagement.highlights", { returnObjects: true }) as string[],
        },
        {
            id: "rotation-control",
            icon: <Rotate90DegreesCcw />,
            title: t("features.items.rotationControl.title"),
            description: t("features.items.rotationControl.description"),
            highlights: t("features.items.rotationControl.highlights", { returnObjects: true }) as string[],
        },
        {
            id: "visual-export",
            icon: <ViewInAr />,
            title: t("features.items.visualExport.title"),
            description: t("features.items.visualExport.description"),
            highlights: t("features.items.visualExport.highlights", { returnObjects: true }) as string[],
        },
    ];

    // Upcoming features
    const UPCOMING_FEATURES: FeatureItem[] = [
        {
            id: "waste-tracking",
            icon: <BarChart />,
            title: t("features.items.wasteTracking.title"),
            description: t("features.items.wasteTracking.description"),
            highlights: t("features.items.wasteTracking.highlights", { returnObjects: true }) as string[],
        },
        {
            id: "template-load",
            icon: <Settings />,
            title: t("features.items.templateLoad.title"),
            description: t("features.items.templateLoad.description"),
            highlights: t("features.items.templateLoad.highlights", { returnObjects: true }) as string[],
        },
        {
            id: "projects-storage",
            icon: <Category />,
            title: t("features.items.projectsStorage.title"),
            description: t("features.items.projectsStorage.description"),
            highlights: t("features.items.projectsStorage.highlights", { returnObjects: true }) as string[],
        },
    ];

    return (
        <Box
            component="section"
            aria-labelledby="features-heading"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: "background.paper",
                position: "relative",
            }}
            {...props}
        >
            <Container maxWidth="lg">
                {/* Section Header */}
                <Box sx={{ textAlign: "center", mb: { xs: 4, md: 8 } }}>
                    <Typography
                        variant="overline"
                        sx={[
                            {
                                fontWeight: 600,
                                letterSpacing: 1,
                                mb: 2,
                                display: "block",
                            },
                            theme =>
                                theme.applyStyles("light", {
                                    color: theme.palette.text.secondary,
                                }),
                            theme =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.6)",
                                }),
                        ]}
                    >
                        {t("features.sectionTitle")}
                    </Typography>
                    <Typography
                        variant="h2"
                        component="h2"
                        id="features-heading"
                        sx={{
                            mb: 3,
                            fontSize: { xs: "2rem", md: "3rem" },
                            fontWeight: 700,
                        }}
                    >
                        {t("features.title.main")}{" "}
                        <Box
                            component="span"
                            sx={[
                                theme =>
                                    theme.applyStyles("light", {
                                        color: theme.palette.secondary.main,
                                    }),
                                theme =>
                                    theme.applyStyles("dark", {
                                        color: theme.palette.secondary.main,
                                    }),
                            ]}
                        >
                            {t("features.title.accent")}
                        </Box>
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={[
                            {
                                maxWidth: 600,
                                mx: "auto",
                                lineHeight: 1.6,
                            },
                            theme =>
                                theme.applyStyles("light", {
                                    color: theme.palette.text.secondary,
                                }),
                            theme =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t("features.subtitle")}
                    </Typography>
                </Box>

                {/* Current Features Grid */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, 1fr)",
                            lg: "repeat(3, 1fr)",
                        },
                        gap: 4,
                        mb: 6,
                    }}
                >
                    {CURRENT_FEATURES.map((feature: FeatureItem, index: number) => (
                        <Card
                            key={feature.id}
                            sx={[
                                {
                                    height: "100%",
                                    transition: "all 0.3s ease",
                                    border: 1,
                                    borderColor: "divider",
                                    position: "relative",
                                    ...(["waste-tracking", "template-load", "projects-storage"].includes(feature.id)
                                        ? {
                                              opacity: 0.8,
                                          }
                                        : {}),
                                },
                                theme =>
                                    theme.applyStyles("light", {
                                        backgroundColor: "background.paper",
                                        "&:hover": {
                                            transform: "translateY(-8px)",
                                            boxShadow: theme.shadows[8],
                                            borderColor: [
                                                "waste-tracking",
                                                "template-load",
                                                "projects-storage",
                                            ].includes(feature.id)
                                                ? theme.palette.secondary.main
                                                : theme.palette.primary.main,
                                            backgroundColor: "background.paper",
                                        },
                                    }),
                                theme =>
                                    theme.applyStyles("dark", {
                                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                                        "&:hover": {
                                            transform: "translateY(-8px)",
                                            boxShadow: theme.shadows[8],
                                            borderColor: [
                                                "waste-tracking",
                                                "template-load",
                                                "projects-storage",
                                            ].includes(feature.id)
                                                ? theme.palette.secondary.main
                                                : theme.palette.primary.main,
                                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                                        },
                                    }),
                            ]}
                        >
                            <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
                                {/* Icon */}
                                <Box
                                    sx={[
                                        {
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            mb: 3,
                                            "& .MuiSvgIcon-root": {
                                                fontSize: "1.75rem",
                                            },
                                        },
                                        theme =>
                                            theme.applyStyles("light", {
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                            }),
                                        theme =>
                                            theme.applyStyles("dark", {
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                            }),
                                    ]}
                                    aria-hidden="true"
                                >
                                    {feature.icon}
                                </Box>{" "}
                                {/* Title */}
                                <Typography
                                    variant="h6"
                                    component="h3"
                                    sx={[
                                        {
                                            mb: 2,
                                            fontWeight: 600,
                                        },
                                        theme =>
                                            theme.applyStyles("light", {
                                                color: theme.palette.text.primary,
                                            }),
                                        theme =>
                                            theme.applyStyles("dark", {
                                                color: "#FFFFFF",
                                            }),
                                    ]}
                                >
                                    {feature.title}
                                </Typography>
                                {/* Description */}
                                <Typography
                                    variant="body1"
                                    sx={[
                                        {
                                            mb: 3,
                                            lineHeight: 1.6,
                                            flexGrow: 1,
                                        },
                                        theme =>
                                            theme.applyStyles("light", {
                                                color: theme.palette.text.secondary,
                                            }),
                                        theme =>
                                            theme.applyStyles("dark", {
                                                color: "rgba(255, 255, 255, 0.8)",
                                            }),
                                    ]}
                                >
                                    {feature.description}
                                </Typography>
                                {/* Highlights */}
                                <Stack
                                    direction="column"
                                    spacing={1}
                                    role="list"
                                    aria-label={`${feature.title} features`}
                                >
                                    {["waste-tracking", "template-load", "projects-storage"].includes(feature.id) && (
                                        <Chip
                                            label="Coming Soon"
                                            size="small"
                                            color="secondary"
                                            sx={{
                                                alignSelf: "flex-start",
                                                mb: 1,
                                                fontWeight: "bold",
                                            }}
                                        />
                                    )}
                                    {feature.highlights.map((highlight, highlightIndex) => (
                                        <Chip
                                            key={highlightIndex}
                                            label={highlight}
                                            size="small"
                                            variant="outlined"
                                            role="listitem"
                                            sx={[
                                                {
                                                    fontSize: "0.75rem",
                                                    height: 24,
                                                    alignSelf: "flex-start",
                                                },
                                                theme =>
                                                    theme.applyStyles("light", {
                                                        borderColor: theme.palette.primary.main,
                                                        color: theme.palette.primary.main,
                                                        "&:hover": {
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: theme.palette.primary.contrastText,
                                                        },
                                                        "&:focus": {
                                                            outline: `2px solid ${theme.palette.primary.main}`,
                                                            outlineOffset: 2,
                                                        },
                                                    }),
                                                theme =>
                                                    theme.applyStyles("dark", {
                                                        borderColor: "rgba(255, 255, 255, 0.3)",
                                                        color: "rgba(255, 255, 255, 0.9)",
                                                        "&:hover": {
                                                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                                                            color: "#FFFFFF",
                                                        },
                                                        "&:focus": {
                                                            outline: "2px solid rgba(255, 255, 255, 0.3)",
                                                            outlineOffset: 2,
                                                        },
                                                    }),
                                            ]}
                                        />
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {/* Upcoming Features Section */}
                <Box sx={{ textAlign: "center", mb: 6 }}>
                    <Typography
                        variant="h4"
                        component="h3"
                        sx={[
                            {
                                mb: 3,
                                fontWeight: 600,
                            },
                            theme =>
                                theme.applyStyles("light", {
                                    color: theme.palette.text.secondary,
                                }),
                            theme =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.6)",
                                }),
                        ]}
                    >
                        {t("features.upcomingTitle")}
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={[
                            {
                                mb: 4,
                                maxWidth: 600,
                                mx: "auto",
                            },
                            theme =>
                                theme.applyStyles("light", {
                                    color: theme.palette.text.secondary,
                                }),
                            theme =>
                                theme.applyStyles("dark", {
                                    color: "rgba(255, 255, 255, 0.8)",
                                }),
                        ]}
                    >
                        {t("features.upcomingSubtitle")}
                    </Typography>
                </Box>

                {/* Upcoming Features Grid */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "repeat(2, 1fr)",
                            lg: "repeat(3, 1fr)",
                        },
                        gap: 4,
                        opacity: 0.7,
                    }}
                >
                    {UPCOMING_FEATURES.map((feature: FeatureItem, index: number) => (
                        <Card
                            key={feature.id}
                            sx={[
                                {
                                    height: "100%",
                                    transition: "all 0.3s ease",
                                    border: 1,
                                    borderColor: "divider",
                                },
                                theme =>
                                    theme.applyStyles("light", {
                                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                                        "&:hover": {
                                            transform: "translateY(-8px)",
                                            boxShadow: theme.shadows[8],
                                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                                            borderColor: theme.palette.secondary.main,
                                        },
                                    }),
                                theme =>
                                    theme.applyStyles("dark", {
                                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                                        borderColor: "rgba(255, 255, 255, 0.12)",
                                        "&:hover": {
                                            transform: "translateY(-8px)",
                                            boxShadow: theme.shadows[8],
                                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                                            borderColor: theme.palette.secondary.main,
                                        },
                                    }),
                            ]}
                        >
                            <CardContent sx={{ p: 4, height: "100%", display: "flex", flexDirection: "column" }}>
                                {/* Icon */}
                                <Box
                                    sx={[
                                        {
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: 56,
                                            height: 56,
                                            borderRadius: 2,
                                            mb: 3,
                                            "& .MuiSvgIcon-root": {
                                                fontSize: "1.75rem",
                                            },
                                            backgroundColor: theme.palette.secondary.main,
                                            color: theme.palette.secondary.contrastText,
                                        },
                                    ]}
                                    aria-hidden="true"
                                >
                                    {feature.icon}
                                </Box>
                                {/* Title */}
                                <Typography
                                    variant="h6"
                                    component="h3"
                                    sx={[
                                        {
                                            mb: 2,
                                            fontWeight: 600,
                                        },
                                        theme =>
                                            theme.applyStyles("light", {
                                                color: theme.palette.text.primary,
                                            }),
                                        theme =>
                                            theme.applyStyles("dark", {
                                                color: "#FFFFFF",
                                            }),
                                    ]}
                                >
                                    {feature.title}
                                </Typography>
                                {/* Description */}
                                <Typography
                                    variant="body1"
                                    sx={[
                                        {
                                            mb: 3,
                                            lineHeight: 1.6,
                                            flexGrow: 1,
                                            color: theme.palette.text.secondary,
                                        },
                                        theme =>
                                            theme.applyStyles("dark", {
                                                color: "#FFFFFF",
                                            }),
                                    ]}
                                >
                                    {feature.description}
                                </Typography>
                                {/* Highlights */}
                                <Stack
                                    direction="column"
                                    spacing={1}
                                    role="list"
                                    aria-label={`${feature.title} features`}
                                >
                                    {feature.highlights.map((highlight: string, highlightIndex: number) => (
                                        <Chip
                                            key={highlightIndex}
                                            label={highlight}
                                            size="small"
                                            variant="outlined"
                                            role="listitem"
                                            sx={[
                                                {
                                                    fontSize: "0.75rem",
                                                    height: 24,
                                                    alignSelf: "flex-start",
                                                    borderColor: theme.palette.secondary.dark,
                                                    color: theme.palette.secondary.dark,
                                                },
                                                theme =>
                                                    theme.applyStyles("dark", {
                                                        borderColor: "#FBBF24",
                                                        color: "#FBBF24",
                                                    }),
                                            ]}
                                        />
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingFeaturesOrganism;
export type { LandingFeaturesOrganismProps };
// #endregion
